import React from 'react';
import { Zap, Clock, Star, AlertTriangle, Sparkles } from 'lucide-react';
import { calcProductionPace } from '../kpiUtils';

const ICON_MAP = { Zap, Clock, Star, AlertTriangle };

const generateInsights = (data) => {
  if (!data) return [];
  const insights = [];
  const {
    limitingFactor,
    teamProgress,
    crewPerformance,
    hasBottleneckEndpoint,
    stats,
    atRiskCount,
    deviceStockVersions,
    shakerStock,
    pumpStock,
  } = data;

  // 1. BOM manufacturing bottleneck
  if (hasBottleneckEndpoint && limitingFactor) {
    insights.push({
      priority: 'critical',
      headline: `${limitingFactor.name} limits device manufacturing`,
      body: `Only ${limitingFactor.current_stock} unit(s) in stock — max ${limitingFactor.max_units_possible} device(s) can be built from components.`,
      action: 'Reorder this component',
      icon: 'Zap',
    });
  }

  // 2. Finished hardware below min alert (current_stock)
  [...(deviceStockVersions || []), shakerStock, pumpStock]
    .filter(Boolean)
    .filter((hw) => hw.isLowStock)
    .forEach((hw) => {
      insights.push({
        priority: 'critical',
        headline: `${hw.label || hw.name} is below minimum stock`,
        body: `Only ${hw.current_stock} unit(s) in stock (min alert: ${hw.min_quantity}). Update stock on the Hardware Devices page after restocking.`,
        action: 'Review hardware inventory',
        icon: 'AlertTriangle',
      });
    });

  if ((stats?.lowStockItems || 0) > 0) {
    insights.push({
      priority: 'critical',
      headline: `${stats.lowStockItems} reagent(s) below minimum stock threshold`,
      body: 'Review reagent inventory to avoid production stoppages.',
      action: 'Review inventory immediately',
      icon: 'AlertTriangle',
    });
  }

  // 2. At-risk orders
  if ((atRiskCount || 0) > 0) {
    insights.push({
      priority: 'high',
      headline: `${atRiskCount} order${atRiskCount !== 1 ? 's' : ''} at risk of missing deadline`,
      body: `${atRiskCount} order${atRiskCount !== 1 ? 's are' : ' is'} due within 3 days and still pending. Prioritise fulfilment now.`,
      action: 'Review at-risk orders',
      icon: 'AlertTriangle',
    });
  }

  // 3. Production pace
  const { workdayProgress, pacePct, projectedEOD } = calcProductionPace(teamProgress.totalAchieved, teamProgress.totalTarget);
  const target90 = Math.round(teamProgress.totalTarget * 0.9);
  if (workdayProgress > 20 && pacePct < 70) {
    const behindBy = Math.round(workdayProgress - teamProgress.percentage);
    insights.push({
      priority: 'warning',
      headline: `Production tracking ${behindBy}% behind expected pace`,
      body: `At current rate, team will hit ~${projectedEOD.toLocaleString()} units by EOD. The 90% target is ${target90.toLocaleString()} units.`,
      action: 'Review crew allocation',
      icon: 'Clock',
    });
  }

  // 4. Top performer
  const sorted = [...(crewPerformance || [])].sort((a, b) => Number(b.percentage || 0) - Number(a.percentage || 0));
  const top = sorted[0];
  if (top && Number(top.percentage || 0) >= 100) {
    insights.push({
      priority: 'positive',
      headline: `${top.worker_name} has exceeded today's target`,
      body: `${top.worker_name} is at ${top.percentage}% of target. Consider reassigning their remaining capacity to the bottleneck area.`,
      action: null,
      icon: 'Star',
    });
  }

  return insights.slice(0, 4);
};

const PRIORITY = {
  critical: { border: 'border-l-red-500', badge: 'bg-red-100 text-red-700', label: 'Critical' },
  high: { border: 'border-l-amber-500', badge: 'bg-amber-100 text-amber-700', label: 'High' },
  warning: { border: 'border-l-yellow-400', badge: 'bg-yellow-100 text-yellow-700', label: 'Attention' },
  positive: { border: 'border-l-emerald-500', badge: 'bg-emerald-100 text-emerald-700', label: 'Positive' },
};

const InsightCard = ({ insight }) => {
  const Icon = ICON_MAP[insight.icon] || Zap;
  const style = PRIORITY[insight.priority] || PRIORITY.high;
  return (
    <div className={`flex gap-3 border-l-4 pl-4 py-1 ${style.border}`}>
      <Icon className="w-4 h-4 shrink-0 mt-0.5 text-slate-500" />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1 flex-wrap">
          <p className="text-sm font-semibold text-slate-800 leading-snug">{insight.headline}</p>
          <span className={`text-[9px] font-bold uppercase px-1.5 py-0.5 rounded ${style.badge}`}>{style.label}</span>
        </div>
        <p className="text-xs text-slate-500 leading-relaxed">{insight.body}</p>
        {insight.action && (
          <p className="text-xs text-slate-600 mt-1.5 font-medium">→ {insight.action}</p>
        )}
      </div>
    </div>
  );
};

export default function AIInsights({ data, embedded = false }) {
  const insights = generateInsights(data);

  const content = (
    <>
      {insights.length === 0 ? (
        <p className="text-sm text-slate-400 italic">No significant issues detected. Operations appear nominal.</p>
      ) : (
        <div className={embedded ? 'flex flex-col gap-4' : 'grid grid-cols-1 md:grid-cols-2 gap-5'}>
          {insights.map((insight, i) => <InsightCard key={i} insight={insight} />)}
        </div>
      )}
      <p className={`text-[10px] text-slate-400 ${embedded ? 'mt-4 pt-3 border-t border-slate-100' : 'mt-5 border-t border-slate-100 pt-3'}`}>
        Generated from live operational data · Rule-based analysis · Auto-refreshes every 5 minutes
      </p>
    </>
  );

  if (embedded) {
    return (
      <div className="border-t border-slate-100 pt-4 h-full flex flex-col">
        <div className="flex items-center gap-2 mb-3">
          <Sparkles className="w-4 h-4 text-violet-500" />
          <h4 className="text-sm font-semibold text-slate-800">AI Operations Insights</h4>
          <span className="text-[9px] bg-violet-100 text-violet-700 px-1.5 py-0.5 rounded-full font-medium uppercase tracking-wide">Live</span>
        </div>
        <div className="flex-1">{content}</div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
      <div className="flex items-center gap-2 px-5 py-4 border-b border-slate-200">
        <Sparkles className="w-4 h-4 text-violet-500" />
        <h3 className="text-base font-semibold text-slate-800">AI Operations Insights</h3>
        <span className="text-[10px] bg-violet-100 text-violet-700 px-2 py-0.5 rounded-full font-medium uppercase tracking-wide">Live</span>
      </div>
      <div className="p-5">{content}</div>
    </div>
  );
}
